import React from 'react';
import { BellIcon, BellSlashIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { usePushNotifications } from '../../hooks/usePushNotifications.ts';

interface PushNotificationToggleProps {
  compact?: boolean;
}

export default function PushNotificationToggle({ compact = false }: PushNotificationToggleProps) {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  // Don't show anything if not supported
  if (!isSupported) {
    if (compact) return null;
    return (
      <div className="bg-gray-100 rounded-lg p-4 text-center">
        <p className="text-sm text-gray-500">
          Push notifications are not supported on this device/browser.
        </p>
      </div>
    );
  }

  // Compact version for sidebars
  if (compact) {
    return (
      <button
        onClick={isSubscribed ? unsubscribe : subscribe}
        disabled={isLoading || permission === 'denied'}
        className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
          isSubscribed
            ? 'bg-green-100 text-green-700 hover:bg-green-200'
            : permission === 'denied'
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
        }`}
        title={
          permission === 'denied'
            ? 'Notifications blocked. Please enable in browser settings.'
            : isSubscribed
            ? 'Click to disable push notifications'
            : 'Click to enable push notifications'
        }
      >
        {isLoading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
        ) : isSubscribed ? (
          <BellIcon className="h-4 w-4 mr-2" />
        ) : (
          <BellSlashIcon className="h-4 w-4 mr-2" />
        )}
        <span className="truncate">
          {isLoading
            ? 'Processing...'
            : permission === 'denied'
            ? 'Notifications Blocked'
            : isSubscribed
            ? 'Notifications On'
            : 'Enable Notifications'}
        </span>
      </button>
    );
  }

  // Full version for settings/profile pages
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-ggaa-green to-green-600 text-white px-6 py-4">
        <div className="flex items-center">
          <DevicePhoneMobileIcon className="h-6 w-6 mr-3" />
          <div>
            <h3 className="font-semibold text-lg">Push Notifications</h3>
            <p className="text-sm text-white/80">Get notified on your phone</p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {permission === 'denied' ? (
          <div className="text-center py-4">
            <BellSlashIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium mb-2">Notifications Blocked</p>
            <p className="text-sm text-gray-500">
              You've blocked notifications for this site. To enable them, update your browser settings.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                {isSubscribed ? (
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <BellIcon className="h-5 w-5 text-green-600" />
                  </div>
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                    <BellSlashIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900">
                    {isSubscribed ? 'Notifications Enabled' : 'Notifications Disabled'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {isSubscribed
                      ? 'You will receive alerts on this device'
                      : 'Enable to receive ranch updates'}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={isSubscribed ? unsubscribe : subscribe}
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                isSubscribed
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-ggaa-green text-white hover:bg-green-700'
              } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2" />
                  Processing...
                </>
              ) : isSubscribed ? (
                <>
                  <BellSlashIcon className="h-5 w-5 mr-2" />
                  Disable Notifications
                </>
              ) : (
                <>
                  <BellIcon className="h-5 w-5 mr-2" />
                  Enable Push Notifications
                </>
              )}
            </button>

            {!isSubscribed && (
              <p className="mt-4 text-xs text-gray-500 text-center">
                You'll be asked to allow notifications. This helps you stay updated with important ranch announcements.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

